// frontend/src/fetchInterceptor.js

// Prevent double install in dev/HMR
if (!window.__FETCH_INTERCEPTOR_INSTALLED__) {
  window.__FETCH_INTERCEPTOR_INSTALLED__ = true;

  const originalFetch = window.fetch;

  // Backend base URL from .env
  const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  const API_ORIGIN = API_BASE ? new URL(API_BASE).origin : "";

  // Public (unauthenticated) backend endpoints
  const PUBLIC_BACKEND_PATHS = [
    "/api/ekyc/verify-token",
    "/api/ekyc/create-session",
    "/api/ekyc/download-tac",
    "/api/client-ekyc/verify-token",
    "/api/client-ekyc/create-session",
    "/api/client-ekyc/download-form",
    "/api/client-ekyc/form-description",
    "/api/client-ekyc/upload-signed-form",
    "/api/forgot-password",
    "/api/email-change/verify",
    "/api/public-market-reports",
    "/api/managed-portfolio-factsheets",
    "/api/bookings",
    "/api/demo/self-register",
    "/health",
  ];

  function toAbsoluteURL(input) {
    if (typeof input === "string") return new URL(input, window.location.origin);
    return new URL(input.url, window.location.origin);
  }

  function isBackendURL(absUrl) {
    return API_ORIGIN && absUrl.origin === API_ORIGIN;
  }

  function isPublicBackendPath(absUrl) {
    if (!isBackendURL(absUrl)) return false;
    return PUBLIC_BACKEND_PATHS.some((p) => absUrl.pathname.startsWith(p));
  }

  window.fetch = async (input, options = {}) => {
    const absUrl = toAbsoluteURL(input);
    const headers = new Headers(options.headers || {});
    const hasBody = !!options.body;
    const isMultipart =
      typeof FormData !== "undefined" && options.body instanceof FormData;

    // Determine whether to attach Authorization
    const backendCall = isBackendURL(absUrl);
    const publicBackendCall = isPublicBackendPath(absUrl);

    // Auto-set content-type for JSON requests (except multipart)
    if (!isMultipart && hasBody && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    // Attach Azure Entra token for protected backend requests
    if (backendCall && !publicBackendCall) {
      try {
        const token = localStorage.getItem("token");
        if (token) headers.set("Authorization", `Bearer ${token}`);
      } catch {
        // no-op
      }
    } else {
      // Ensure no token sent to public or third-party endpoints
      headers.delete("Authorization");
      headers.delete("X-MFA-Token");
    }

    const resp = await originalFetch(input, { ...options, headers });

    // Handle unauthorized responses (401)
    // Clear token and let React components (ProtectedRoute, HomeRedirect) handle the redirect.
    // Do NOT use window.location.href — it causes full page reloads that race with React state.
    if (resp.status === 401 && backendCall && !publicBackendCall) {
      localStorage.removeItem("token");
      localStorage.removeItem("userRole");
      localStorage.removeItem("userData");
      try {
        const body = await resp.clone().json();
        const code = body?.errorCode || "";
        if (code === "TOKEN_EXPIRED") {
          window.dispatchEvent(new CustomEvent('app-toast', { detail: { type: 'error', message: 'Your session has expired. Please log in again.' } }));
        } else if (code === "INVALID_TOKEN") {
          window.dispatchEvent(new CustomEvent('app-toast', { detail: { type: 'error', message: 'Invalid session. Please log in again.' } }));
        }
      } catch {
        // ignore parse errors
      }
    }

    return resp;
  };
}
